/**
 * Утилита для работы с локальным хранилищем фотографий (IndexedDB)
 * Обеспечивает надёжное сохранение фото даже при отсутствии интернета
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'CanifolePhotosDB';
const DB_VERSION = 1;
const STORE_NAME = 'photos';

export type PhotoSyncStatus = 'pending' | 'uploading' | 'synced' | 'failed';

export interface PhotoRecord {
  id: string; // UUID
  plantingId: number | string;
  blob: Blob;
  thumbnailBlob?: Blob;
  fileName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  syncStatus: PhotoSyncStatus;
  syncedAt?: string;
  remoteUrl?: string;
  retryCount: number;
  lastError?: string;
  metadata?: {
    saveId?: string;
    description?: string;
    location?: { latitude: number; longitude: number };
    tags?: string[];
  };
}

let dbPromise: Promise<IDBPDatabase> | null = null;

const isClosingError = (error: any) =>
  error?.name === 'InvalidStateError' || /connection is closing/i.test(error?.message);

/**
 * Инициализация базы данных IndexedDB
 */
async function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('plantingId', 'plantingId', { unique: false });
          store.createIndex('syncStatus', 'syncStatus', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      },
    }).then((db) => {
      db.addEventListener('versionchange', () => {
        db.close();
        dbPromise = null;
      });
      db.addEventListener('close', () => {
        dbPromise = null;
      });
      return db;
    });
  }
  return dbPromise;
}

async function withDb<T>(operation: (db: IDBPDatabase) => Promise<T>, retries = 1): Promise<T> {
  try {
    const db = await getDB();
    return await operation(db);
  } catch (error) {
    if (retries > 0 && isClosingError(error)) {
      dbPromise = null;
      const db = await getDB();
      return operation(db);
    }
    throw error;
  }
}

/**
 * Генерация UUID для идентификатора фото
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Сохранение фото в локальное хранилище
 */
export async function savePhotoLocally(
  plantingId: number | string,
  file: File,
  thumbnail?: Blob,
  metadata?: PhotoRecord['metadata']
): Promise<PhotoRecord> {
  return withDb(async (db) => {
    const record: PhotoRecord = {
      id: generateId(),
      plantingId,
      blob: file,
      thumbnailBlob: thumbnail,
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
      createdAt: new Date().toISOString(),
      syncStatus: 'pending',
      retryCount: 0,
      metadata,
    };

    await db.put(STORE_NAME, record);
    return record;
  });
}

/**
 * Получение всех фото для определённой посадки
 */
export async function getPhotosByPlanting(plantingId: number | string): Promise<PhotoRecord[]> {
  return withDb(async (db) => {
    const index = db.transaction(STORE_NAME).store.index('plantingId');
    return index.getAll(plantingId);
  });
}

/**
 * Получение всех фото с определённым статусом
 */
export async function getPhotosByStatus(status: PhotoSyncStatus): Promise<PhotoRecord[]> {
  return withDb(async (db) => {
    const index = db.transaction(STORE_NAME).store.index('syncStatus');
    return index.getAll(status);
  });
}

/**
 * Получение всех несинхронизированных фото (pending + failed)
 */
export async function getPendingPhotos(): Promise<PhotoRecord[]> {
  const pending = await getPhotosByStatus('pending');
  const failed = await getPhotosByStatus('failed');
  return [...pending, ...failed].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

/**
 * Обновление статуса фото
 */
export async function updatePhotoStatus(
  id: string,
  status: PhotoSyncStatus,
  options?: {
    remoteUrl?: string;
    error?: string;
  }
): Promise<void> {
  await withDb(async (db) => {
    const record = await db.get(STORE_NAME, id);
    
    if (!record) {
      throw new Error(`Photo with id ${id} not found`);
    }

    record.syncStatus = status;
    
    if (status === 'synced') {
      record.syncedAt = new Date().toISOString();
      if (options?.remoteUrl) {
        record.remoteUrl = options.remoteUrl;
      }
    }
    
    if (status === 'failed') {
      record.retryCount += 1;
      if (options?.error) {
        record.lastError = options.error;
      }
    }

    await db.put(STORE_NAME, record);
  });
}

/**
 * Получение конкретного фото по ID
 */
export async function getPhotoById(id: string): Promise<PhotoRecord | undefined> {
  return withDb((db) => db.get(STORE_NAME, id));
}

/**
 * Удаление фото из локального хранилища
 */
export async function deletePhoto(id: string): Promise<void> {
  await withDb(async (db) => {
    await db.delete(STORE_NAME, id);
  });
}

/**
 * Удаление всех синхронизированных фото (для очистки хранилища)
 */
export async function cleanupSyncedPhotos(olderThanDays: number = 7): Promise<number> {
  const synced = await getPhotosByStatus('synced');
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
  
  let deletedCount = 0;
  
  for (const photo of synced) {
    if (photo.syncedAt && new Date(photo.syncedAt) < cutoffDate) {
      await deletePhoto(photo.id);
      deletedCount++;
    }
  }
  
  return deletedCount;
}

/**
 * Получение статистики по хранилищу
 */
export async function getStorageStats(): Promise<{
  total: number;
  pending: number;
  uploading: number;
  synced: number;
  failed: number;
  totalSize: number;
}> {
  return withDb(async (db) => {
    const allPhotos = await db.getAll(STORE_NAME);
    
    const stats = {
      total: allPhotos.length,
      pending: 0,
      uploading: 0,
      synced: 0,
      failed: 0,
      totalSize: 0,
    };
    
    allPhotos.forEach((photo) => {
      stats[photo.syncStatus]++;
      stats.totalSize += photo.size;
    });
    
    return stats;
  });
}

/**
 * Получение URL для preview фото из Blob
 */
export function createPhotoUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

/**
 * Освобождение URL после использования
 */
export function revokePhotoUrl(url: string): void {
  URL.revokeObjectURL(url);
}
